import { useCallback } from 'react';
import { type CohortForAdminListsAttrs } from 'Cohorts';
import { type SubmissionsFilters } from '../api';
import { toArray, normalizeArrayFilterValue } from './filterUtils';

/**
 * Array filter keys that use the `filterType-value` chip key format.
 * These filters support multiple selections and chip deletion by value.
 */
const ARRAY_FILTER_KEYS = [
    'saoDisplayStatus',
    'gradingStatus',
    'programType',
    'cohortId',
    'requirementIdentifier',
    'graderId',
    'projectVariant',
    'score',
] as const;

type ArrayFilterKey = (typeof ARRAY_FILTER_KEYS)[number];

function isArrayFilterKey(key: string): key is ArrayFilterKey {
    return ARRAY_FILTER_KEYS.includes(key as ArrayFilterKey);
}

interface UseSubmissionsFilterStateOptions {
    filters: SubmissionsFilters;
    onFiltersChange: (filters: SubmissionsFilters) => void;
    /** All cohorts for validating cohort selection when program type changes */
    allCohorts: CohortForAdminListsAttrs[];
}

interface UseSubmissionsFilterStateReturn {
    /**
     * Update a filter value. Handles cohort invalidation when program type changes.
     */
    handleFilterChange: <K extends keyof SubmissionsFilters>(key: K, value: SubmissionsFilters[K]) => void;

    /**
     * Clear a filter or remove a specific value from an array filter.
     * @param key - The filter key to clear
     * @param valueToRemove - Optional specific value to remove from an array filter
     */
    clearFilter: (key: keyof SubmissionsFilters, valueToRemove?: string) => void;

    /**
     * Handle chip deletion by parsing the chip key format.
     * Array filters use `filterType-value` format, others use just the key.
     */
    handleChipDelete: (chipKey: string) => void;

    /**
     * Clear all filters.
     */
    handleClearAll: () => void;
}

/**
 * Hook for managing submissions filter state with shared logic.
 * Handles filter changes, chip deletion, and cohort validation.
 */
// oxlint-disable-next-line max-lines-per-function
export function useSubmissionsFilterState({
    filters,
    onFiltersChange,
    allCohorts,
}: UseSubmissionsFilterStateOptions): UseSubmissionsFilterStateReturn {
    const handleFilterChange = useCallback(
        <K extends keyof SubmissionsFilters>(key: K, value: SubmissionsFilters[K]) => {
            const newFilters = { ...filters };
            if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                delete newFilters[key];
            } else {
                newFilters[key] = value;
            }
            // Clear cohort filter if program type changes (cohorts may no longer be valid)
            if (key === 'programType' && newFilters.cohortId) {
                const programTypes = toArray(value as string[]);
                const cohortIds = toArray(newFilters.cohortId);
                const validCohortIds = cohortIds.filter(cohortId =>
                    allCohorts.some(
                        c => c.id === cohortId && (programTypes.length === 0 || programTypes.includes(c.programType)),
                    ),
                );
                if (validCohortIds.length === 0) {
                    delete newFilters.cohortId;
                } else if (validCohortIds.length !== cohortIds.length) {
                    newFilters.cohortId = normalizeArrayFilterValue(validCohortIds);
                }
            }
            onFiltersChange(newFilters);
        },
        [filters, onFiltersChange, allCohorts],
    );

    const clearFilter = useCallback(
        (key: keyof SubmissionsFilters, valueToRemove?: string) => {
            const newFilters = { ...filters };
            const currentValue = newFilters[key];

            if (valueToRemove && Array.isArray(currentValue)) {
                // Remove specific value from array
                const newArray = (currentValue as string[]).filter(v => String(v) !== valueToRemove);
                if (newArray.length === 0) {
                    delete newFilters[key];
                } else {
                    (newFilters as Record<string, unknown>)[key] = normalizeArrayFilterValue(newArray);
                }
            } else {
                delete newFilters[key];
            }
            onFiltersChange(newFilters);
        },
        [filters, onFiltersChange],
    );

    const handleChipDelete = useCallback(
        (chipKey: string) => {
            // Parse the key to extract the filter type and value
            const [filterType, ...valueParts] = chipKey.split('-');
            const value = valueParts.join('-'); // Rejoin in case value contains dashes

            if (isArrayFilterKey(filterType)) {
                clearFilter(filterType, value);
            } else {
                // For non-array filters (studentEmail, olderThanDays)
                clearFilter(chipKey as keyof SubmissionsFilters);
            }
        },
        [clearFilter],
    );

    const handleClearAll = useCallback(() => {
        // Pass explicit empty gradingStatus to distinguish from "fresh load"
        // This ensures the URL includes ?gradingStatus= which signals "show all"
        // rather than applying default filters
        onFiltersChange({ gradingStatus: [] });
    }, [onFiltersChange]);

    return {
        handleFilterChange,
        clearFilter,
        handleChipDelete,
        handleClearAll,
    };
}
